import type { StepUpFailure } from '@/lib/step-up'
import type { CreditLedgerKind } from '@/types/generated/public_api'

/** One credit buys this many rows of the data API. */
export const ROWS_PER_CREDIT = 10_000

// Credits, amounts and timestamps arrive as int64 strings (ts-proto forceLong=string); the
// service layer turns them into these number-typed views once, at the boundary.

/** Credits are one Stripe price per credit; the buyer picks the quantity. */
export interface CreditPriceView {
  /** Minor units of `currency` per credit (10 = $0.10). */
  unitAmount: number
  /** ISO 4217, lowercase as Stripe sends it. */
  currency: string
  /** A top-up or a refill buys between these, inclusive. */
  minCredits: number
  maxCredits: number
  /** One-click top-up sizes, in minor units of `currency`. */
  presetAmounts: number[]
}

export interface CreditPreset {
  /** Minor units of the price's currency. */
  amount: number
  credits: number
}

/** Whether `credits` is a quantity the server will sell: a whole number within min..max. */
export function isSellableCredits(price: CreditPriceView, credits: number): boolean {
  return Number.isInteger(credits) && credits >= price.minCredits && credits <= price.maxCredits
}

/**
 * The preset buttons as credit quantities: `amount / unit_amount`, rounded down (the server's
 * rule), dropping any that fall outside min..max after a repricing.
 */
export function creditPresets(price: CreditPriceView): CreditPreset[] {
  if (price.unitAmount <= 0) return []
  return price.presetAmounts
    .map((amount) => ({ amount, credits: Math.floor(amount / price.unitAmount) }))
    .filter((preset) => isSellableCredits(price, preset.credits))
}

export interface CreditSettings {
  /** Unset = no weekly limit. */
  spendLimitWeekly?: number
  refillEnabled: boolean
  refillThreshold: number
  /** Credits each refill buys. 0 = not chosen yet. */
  refillCredits: number
}

export interface CreditLedgerRow {
  kind: CreditLedgerKind
  /** Signed: spend and clawback are negative. */
  credits: number
  createdAtMs: number
  reference: string
  keyId: string
  source: string
  dataset: string
}

/**
 * Whether the server will ask for a step-up (oems_v2 billing/credits.rs `needs_step_up`):
 * raising or removing the limit, turning refill on, or — with refill staying on — raising the
 * refill amount or the threshold. A first limit, lowering it, and turning refill off are free.
 * The server decides against its own read; this only picks which screen to show first.
 */
export function needsStepUp(prev: CreditSettings, next: CreditSettings): boolean {
  const before = prev.spendLimitWeekly
  const after = next.spendLimitWeekly
  if (before !== undefined && (after === undefined || after > before)) return true
  if (!next.refillEnabled) return false
  if (!prev.refillEnabled) return true
  return next.refillCredits > prev.refillCredits || next.refillThreshold > prev.refillThreshold
}

export type SettingsError =
  | 'limitNotPositive'
  | 'refillNeedsLimit'
  | 'refillUnavailable'
  | 'refillCreditsRange'
  | 'thresholdRange'
  | 'refillNeedsCard'

export type SettingsErrors = Partial<
  Record<'limit' | 'refillCredits' | 'threshold' | 'refill', SettingsError>
>

/** The server's settings rules, mirrored so the form never spends a step-up on a refusal. */
export function validateSettings(
  next: CreditSettings,
  price: CreditPriceView | undefined,
  hasCard: boolean
): SettingsErrors {
  const errors: SettingsErrors = {}
  const limit = next.spendLimitWeekly
  if (limit !== undefined && !(Number.isInteger(limit) && limit > 0)) {
    errors.limit = 'limitNotPositive'
  }
  // Amount and threshold only matter while refill is on: credits going off sale must never
  // stop the user from turning refill off.
  if (!next.refillEnabled) return errors

  if (limit === undefined) errors.limit = 'refillNeedsLimit'
  if (!hasCard) errors.refill = 'refillNeedsCard'
  if (!price) {
    errors.refillCredits = 'refillUnavailable'
    return errors
  }
  if (!isSellableCredits(price, next.refillCredits)) {
    errors.refillCredits = 'refillCreditsRange'
    return errors
  }
  const threshold = next.refillThreshold
  if (!Number.isInteger(threshold) || threshold < 1 || threshold > next.refillCredits) {
    errors.threshold = 'thresholdRange'
  }
  return errors
}

export type SaveFailureRoute =
  | { action: 'verify'; factor?: 'totp' | 'email' }
  | { action: 'fieldError'; field: 'currentPassword' | 'totpCode' | 'emailCode' }
  | { action: 'conflict' }
  | { action: 'message'; message: string }

/**
 * Where a refused save lands. A save sent without a step-up (the client predicted none) that
 * the server wanted one for comes back as "Invalid current password." on a password account —
 * the empty password is checked first — or as *_REQUIRED; either way the user belongs on the
 * verify step, not staring at an error for a field that isn't on screen.
 */
export function routeSaveFailure(failure: StepUpFailure, onVerifyStep: boolean): SaveFailureRoute {
  switch (failure.kind) {
    case 'totp-required':
      return { action: 'verify', factor: 'totp' }
    case 'email-code-required':
      return { action: 'verify', factor: 'email' }
    case 'password':
      return onVerifyStep
        ? { action: 'fieldError', field: 'currentPassword' }
        : { action: 'verify' }
    case 'totp-invalid':
      return onVerifyStep ? { action: 'fieldError', field: 'totpCode' } : { action: 'verify' }
    case 'email-code-invalid':
      return onVerifyStep ? { action: 'fieldError', field: 'emailCode' } : { action: 'verify' }
    case 'conflict':
      return { action: 'conflict' }
    case 'other':
      return { action: 'message', message: failure.message }
  }
}

const DAY_MS = 24 * 60 * 60 * 1000

/** `YYYY-MM-DD` of the instant in UTC. */
export function utcDay(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function utcMidnight(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

/** Monday 00:00 UTC of the week containing `now` — where the weekly limit counts from. */
function weekStartMs(now: Date): number {
  const daysSinceMonday = (now.getUTCDay() + 6) % 7
  return utcMidnight(now) - daysSinceMonday * DAY_MS
}

export function weekStartDay(now: Date): string {
  return utcDay(new Date(weekStartMs(now)))
}

/** When the weekly limit next resets: the next Monday 00:00 UTC, strictly after `now`. */
export function nextWeeklyReset(now: Date): Date {
  return new Date(weekStartMs(now) + 7 * DAY_MS)
}

export const STATS_PRESETS = ['week', '30d', '90d'] as const
export type StatsPreset = (typeof STATS_PRESETS)[number]

/** Request range for a preset. "This week" sends neither day — the server's default. */
export function statsRange(preset: StatsPreset, now: Date): { fromDay?: string; toDay?: string } {
  if (preset === 'week') return {}
  const days = preset === '30d' ? 30 : 90
  const today = utcMidnight(now)
  return { fromDay: utcDay(new Date(today - (days - 1) * DAY_MS)), toDay: utcDay(new Date(today)) }
}

/** Every UTC day from `fromDay` to `toDay` inclusive; the server omits days with no spend. */
export function fillSpendGaps<T extends { day: string; total: number }>(
  days: readonly T[],
  fromDay: string,
  toDay: string
): { day: string; total: number }[] {
  const byDay = new Map(days.map((d) => [d.day, d.total]))
  const out: { day: string; total: number }[] = []
  const end = Date.parse(`${toDay}T00:00:00Z`)
  for (let t = Date.parse(`${fromDay}T00:00:00Z`); t <= end; t += DAY_MS) {
    const day = utcDay(new Date(t))
    out.push({ day, total: byDay.get(day) ?? 0 })
  }
  return out
}

/**
 * Balance right after each entry. Entries are newest first and an account's entries sum to its
 * balance, so each one's balance is today's minus everything that came after it. Only meaningful
 * for the unfiltered history — a key filter leaves grants out.
 */
export function runningBalances(
  entries: readonly { credits: number }[],
  balance: number
): number[] {
  let after = balance
  return entries.map((entry) => {
    const current = after
    after -= entry.credits
    return current
  })
}

const LEDGER_PAGE_DEFAULT = 50
// The server cap. With a key filter most rows are someone else's, so ask for as many as allowed.
const LEDGER_PAGE_FILTERED = 200
const LEDGER_MAX_HOPS = 10

export interface LedgerPageRequest {
  keyId?: string
  cursor?: string
  limit: number
}

/**
 * One page of history as the UI sees it. A key filter can return empty pages while the cursor
 * is still set, so keep going until something turns up or the cursor runs out — an empty page
 * never means the end. The hop cap hands back the cursor so "Load more" stays live.
 */
export async function fetchLedgerPage<E>(
  fetchPage: (req: LedgerPageRequest) => Promise<{ entries: E[]; nextCursor?: string }>,
  { keyId, cursor }: { keyId?: string; cursor?: string }
): Promise<{ entries: E[]; nextCursor?: string }> {
  const limit = keyId ? LEDGER_PAGE_FILTERED : LEDGER_PAGE_DEFAULT
  let next = cursor
  for (let hop = 0; hop < LEDGER_MAX_HOPS; hop++) {
    const page = await fetchPage({ keyId, cursor: next, limit })
    if (page.entries.length > 0 || !page.nextCursor) {
      return { entries: page.entries, nextCursor: page.nextCursor }
    }
    next = page.nextCursor
  }
  return { entries: [], nextCursor: next }
}

/** A Stripe minor-unit amount in major units, using the currency's own exponent (JPY has 0). */
export function formatMinorUnits(
  amount: number,
  currency: string
): { value: number; currency: string } {
  const code = currency.toUpperCase()
  let digits = 2
  try {
    digits =
      new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).resolvedOptions()
        .maximumFractionDigits ?? 2
  } catch {
    // Unknown code: Stripe's default exponent.
  }
  return { value: amount / 10 ** digits, currency: code }
}
